import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { Button, Icon, useErrorDialog } from '@dayon/design-system';
import { GAME_PICTURE_ACCEPT } from '@dayon/contracts';
import type { PendingGameConfig } from './GameConfigDialog.tsx';
import {
  checkPictureDecoded,
  checkPictureFile,
  pickMayStage,
  PICTURE_BROKEN,
  PICTURE_GUIDANCE,
  PICTURE_INPUT_LABEL,
  PICTURE_NONE,
  pictureAlt,
  picturePreviewSource,
  pictureSummary,
  revertWords,
  STAGED_UPLOAD_SENTENCE,
  type StagedPicture,
} from './game-picture.ts';

/**
 * THE PICTURE SECTION of Edit configuration.
 *
 * It STAGES and nothing more. Choosing a file reads it in this browser — its
 * type, its size, and whether it decodes as a picture at all — and hands the
 * dialog a staged picture; going back hands it a staged return. The upload
 * link, the upload and the confirm all wait for the dialog's one Save, under the
 * one reason the dialog asks for. NOTHING HERE FETCHES, on mount or after: this
 * console once shipped effects that fetched on mount even behind an early
 * return, and a section that asks for nothing cannot repeat it.
 *
 * Every sentence it shows and every decision it makes comes from
 * `game-picture.ts`, where a test pins it; refusals open the error popup and
 * stage nothing.
 */

/** 4 wide by 3 tall, cropped to the middle: what the player's tile draws (`GameCard.tsx`). */
const previewBox: CSSProperties = {
  width: '100%',
  maxWidth: '240px',
  aspectRatio: '4 / 3',
  flex: 'none',
  display: 'grid',
  placeItems: 'center',
  overflow: 'hidden',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-divider)',
  background: 'var(--color-surface)',
};
const previewImage: CSSProperties = { width: '100%', height: '100%', objectFit: 'cover', display: 'block' };
const previewWords: CSSProperties = {
  padding: 'var(--space-3)',
  textAlign: 'center',
  fontSize: '12px',
};
const heading: CSSProperties = {
  margin: 0,
  fontSize: '12.5px',
  fontWeight: 500,
  color: 'var(--color-text-muted)',
};
const small: CSSProperties = { margin: 0, fontSize: '12px' };

/**
 * The picture's width and height, or null when the browser cannot read the file
 * as a picture — a renamed text file, a damaged one, or a browser without the
 * decoder. Decoded locally; nothing is sent.
 */
async function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  try {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

export function GamePictureSection({
  game,
  staged,
  onStage,
  disabled,
}: {
  game: PendingGameConfig;
  staged: StagedPicture | null;
  onStage: (next: StagedPicture | null) => void;
  disabled: boolean;
}) {
  const { showError } = useErrorDialog();
  const headingId = useId();
  const notReadyId = useId();
  const revertHintId = useId();
  const picker = useRef<HTMLInputElement>(null);
  /* Reading a file takes a moment, and three things can happen meanwhile: the
     dialog closes (a picture staged into it would hold an object URL nothing
     revokes), Save is pressed, or another file is chosen. `live`, `saving` and
     the pick counter are what `pickMayStage` judges a finished read against. */
  const live = useRef(true);
  const saving = useRef(disabled);
  const picks = useRef(0);
  /* The address that failed to load, so a new address gets a fresh try without an effect. */
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);

  const summary = pictureSummary({
    providerName: game.provider,
    driver: game.driver,
    iconSource: game.iconSource,
    iconUrl: game.iconUrl,
    providerIconUrl: game.providerIconUrl,
    uploadsReady: game.uploadsReady,
  });
  const src = picturePreviewSource(game, staged);
  /* What going back will do: beside its button before it is pressed, in the notice after. */
  const revert = revertWords(summary, staged);
  const previewUrl = staged?.kind === 'UPLOAD' ? staged.previewUrl : null;

  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  useEffect(() => {
    saving.current = disabled;
  }, [disabled]);

  /* A chosen file's object URL is released when another replaces it, when it is
     dropped, and when the section goes (the dialog closed or saved). */
  useEffect(() => {
    if (previewUrl === null) return;
    return () => URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const pick = async (file: File): Promise<void> => {
    /* Numbered at once, so a newer choice supersedes a read still in progress. */
    picks.current += 1;
    const mine = picks.current;
    const typed = checkPictureFile(file);
    if (!typed.ok) {
      showError({ title: typed.title, message: typed.message });
      return;
    }
    const seen = checkPictureDecoded(await readDimensions(file));
    if (
      !pickMayStage({ pick: mine, latestPick: picks.current, saving: saving.current, open: live.current })
    ) {
      return;
    }
    if (!seen.ok) {
      showError({ title: seen.title, message: seen.message });
      return;
    }
    onStage({
      kind: 'UPLOAD',
      file,
      contentType: typed.contentType,
      previewUrl: URL.createObjectURL(file),
      advice: seen.advice,
    });
  };

  const choose = () => picker.current?.click();
  const uploadOff = disabled || !game.uploadsReady;

  return (
    <section className="stack" aria-labelledby={headingId} style={{ gap: 'var(--space-3)' }}>
      <h3 id={headingId} style={heading}>
        Picture
      </h3>

      <div style={previewBox}>
        {src !== null && brokenSrc !== src ? (
          <img
            src={src}
            alt={pictureAlt(game.name)}
            style={previewImage}
            onError={() => setBrokenSrc(src)}
          />
        ) : (
          <span className="text-subtle" style={previewWords}>
            {src === null ? PICTURE_NONE : PICTURE_BROKEN}
          </span>
        )}
      </div>

      <div className="stack" style={{ gap: 'var(--space-1)' }}>
        <p style={{ margin: 0 }}>
          <strong>{summary.sentence}</strong>
        </p>
        <p className="text-subtle" style={small}>
          {summary.providerLine}
        </p>
      </div>

      <ul className="text-subtle stack" style={{ ...small, gap: 'var(--space-1)', paddingLeft: 'var(--space-4)' }}>
        {PICTURE_GUIDANCE.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <input
        ref={picker}
        type="file"
        accept={GAME_PICTURE_ACCEPT}
        aria-label={PICTURE_INPUT_LABEL}
        className="sr-only"
        tabIndex={-1}
        disabled={uploadOff}
        onChange={(e) => {
          const file = e.target.files?.[0];
          /* Cleared at once, so choosing the same file again is still a change. */
          e.target.value = '';
          if (file) void pick(file);
        }}
      />

      {staged?.kind === 'UPLOAD' && (
        <>
          <div className="notice">
            <Icon name="image" />
            <div className="stack" style={{ gap: 'var(--space-1)' }}>
              <span>{STAGED_UPLOAD_SENTENCE}</span>
              {staged.advice.map((line) => (
                <span key={line} className="text-subtle">
                  {line}
                </span>
              ))}
            </div>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button small onClick={choose} disabled={uploadOff}>
              Choose another
            </Button>
            <Button small variant="ghost" onClick={() => onStage(null)} disabled={disabled}>
              Don&apos;t use this picture
            </Button>
          </div>
        </>
      )}

      {staged?.kind === 'REVERT' && (
        <>
          <div className="notice">
            <Icon name="arrow-counter-clockwise" />
            <div>{revert.stagedNotice}</div>
          </div>
          <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button small onClick={() => onStage(null)} disabled={disabled}>
              Keep the uploaded picture
            </Button>
          </div>
        </>
      )}

      {staged === null && (
        <div className="row" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button
            small
            onClick={choose}
            disabled={uploadOff}
            aria-describedby={summary.notReadySentence ? notReadyId : undefined}
          >
            <Icon name="upload-simple" />
            Upload a picture
          </Button>
          {summary.canRevert && (
            <Button
              small
              variant="ghost"
              onClick={() => onStage({ kind: 'REVERT' })}
              disabled={disabled}
              aria-describedby={revert.buttonHint ? revertHintId : undefined}
            >
              <Icon name="arrow-counter-clockwise" />
              Use the provider&apos;s picture again
            </Button>
          )}
        </div>
      )}

      {revert.buttonHint && (
        <p className="text-subtle" id={revertHintId} style={small}>
          {revert.buttonHint}
        </p>
      )}

      {summary.notReadySentence && (
        <div className="notice notice-quiet" id={notReadyId}>
          <Icon name="info" />
          <div>{summary.notReadySentence}</div>
        </div>
      )}
    </section>
  );
}
